// The composed labels of areas 30 and 40. Here and not beside the pages: a page file may
// export only components (`react-refresh/only-export-components`).

import {
  type AppointmentRole,
  type ClubPlayerRead,
  type MatchRead,
  type MatchSide,
  type TeamEnrollmentRead,
  type TeamRead,
} from '@volee/api-client'
import {
  initialsFromPersonName,
  type AvatarFace,
  type MatchHeaderSide,
  type TeamCardCount,
  type TeamCardPlace,
  type TeamCardProps,
} from '@volee/ui'

/** What `squadLabel` reads, and no more: also fits `TeamBrief`, a tesseramento's squad with
 *  no timestamps. */
export type SquadFields = Pick<TeamRead, 'age_group' | 'under_age' | 'gender' | 'letter'>

import {
  AGE_GROUPS,
  APPOINTMENT_ROLES,
  interpolate,
  MATCH_STATUSES,
  messages,
  positionLabel,
  positionShortLabel,
  SQUAD_COACH_NOUN,
  SQUAD_PLAYER_NOUNS,
  TEAM_GENDERS,
  translateDomainValue,
  type PlayerPosition,
} from '../messages/index.ts'
import { entriesOf } from './leagues.ts'

/** The two names a person record keeps apart. Structural, so `AppointmentRead`, `PersonRead`,
 *  `ClubPlayerRead` and `OrgChartEntry` all fit. */
export interface PersonNames {
  readonly last_name: string
  readonly first_name: string
}

/** How a person reads in a list: surname first, the order every table in the product scans
 *  by. */
export function personName(person: PersonNames): string {
  return `${person.last_name} ${person.first_name}`
}

/** The order every roster of the app reads in: surname, then given name, by Italian collation. */
export function bySurname(a: PersonNames, b: PersonNames): number {
  return (
    a.last_name.localeCompare(b.last_name, 'it') || a.first_name.localeCompare(b.first_name, 'it')
  )
}

/** The circle of a person: their initials, given name first whatever the order a row writes the
 *  name in, and the photo the server signed — which it does not when there is none, or when the
 *  reader may not see it, and nothing here says which. */
export function faceOf(
  person: PersonNames & { readonly photo_display_url?: string | null },
): AvatarFace {
  return {
    initials: initialsFromPersonName(person.first_name, person.last_name),
    photoUrl: person.photo_display_url ?? undefined,
  }
}

type DetailPart = string | number | null | undefined

/** A line of its parts, «DIF · 2011 · Giovanissimi»: what is missing is left out, and a line of
 *  nothing is no line (`undefined`) — a row draws no empty span for it. A line that starts with a
 *  part that is there is always one. */
export function detailLine(parts: readonly [string, ...DetailPart[]]): string
export function detailLine(parts: readonly DetailPart[]): string | undefined
export function detailLine(parts: readonly DetailPart[]): string | undefined {
  const present = parts.filter((part) => part != null && part !== '')
  return present.length === 0 ? undefined : present.join(' · ')
}

/** A person as one speaks of them, given name first: «Paolo Ferrante». The lists scan by surname
 *  (`personName`); a line one reads aloud, like the coaches under a squad's title, does not. */
export function personFullName(person: PersonNames): string {
  return `${person.first_name} ${person.last_name}`
}

/** The letter of a club's first side, which the federation leaves unwritten: on C.U. 44 of
 *  Chiavari (`services/api/tests/fixtures/chiavari-cu44-2526.pdf`) it is the bare club name,
 *  and `SQ.B` onwards name the others. */
const FIRST_SQUAD_LETTER = 'A'

/** Said only when it says something: a club fields one side per category and gender, so «Squadra
 *  A» on every squad would be noise on most of them — and the dirigente's bulletin omits it too. */
export function squadLetter(letter: string): string | undefined {
  if (letter === FIRST_SQUAD_LETTER) return undefined
  return interpolate(messages.team.squadLetter, { letter })
}

/** A squad as every form and list of the società's pages names it. Why all three segments, the gender
 *  included: CODING_STANDARDS.md § «Convenzioni di codice delle app». The letter is the fourth,
 *  and the only thing that tells a club's two sides of one category apart. */
export function squadLabel(team: SquadFields): string {
  return detailLine([squadCategoryLabel(team), squadLetter(team.letter)])
}

/** The label without the letter: what a club's A and B share, «Pulcini · U11 · Mista». */
export function squadCategoryLabel(team: Omit<SquadFields, 'letter'>): string {
  const category = translateDomainValue(AGE_GROUPS, team.age_group)
  const gender = translateDomainValue(TEAM_GENDERS, team.gender)
  return detailLine([category, underAgeShort(team), gender])
}

/** «U17», «1° e 2° anno», or nothing for the seniores: under_age 0 means either of the last two. */
export function underAgeShort(team: Pick<SquadFields, 'age_group' | 'under_age'>): string | null {
  if (team.age_group === 'seniores') return null
  if (team.under_age === 0) return messages.team.bothYears
  return `U${String(team.under_age)}`
}

/** La leva of a tesseramento or of a candidate. «The first» and not «the only» because a
 *  row written before «una leva sola» can hold two, and the API orders them so that the
 *  squad named here is the one its refusals name too. */
export function heldSquad<T extends SquadFields>(holder: {
  readonly teams?: readonly T[]
}): T | undefined {
  return (holder.teams ?? [])[0]
}

/** The glossary's word for a squad's players, for its gender and for a number: «giocatore»,
 *  «giocatrici». The one place the word is picked, so the card, the badge, the count of the
 *  roster page and the head's tile cannot name the same number differently. */
function playersNoun(team: Pick<TeamRead, 'gender'>, count: number): string {
  const words = SQUAD_PLAYER_NOUNS[team.gender]
  return count === 1 ? words.one : words.many
}

/** The rosa on a squad's card: how many, in the word for the squad's gender and for that
 *  number — or nothing, to a caller the count is withheld from: a null is not a zero. */
export function squadPlayers(team: TeamRead): TeamCardCount | undefined {
  if (team.players_count == null) return undefined
  return { count: team.players_count, noun: playersNoun(team, team.players_count) }
}

/** The club a squad is drawn with: the name its shield's initials come from, and its crest. */
export interface SquadClub {
  readonly name: string
  readonly crestUrl: string | undefined
}

/** What a squad's `TeamCard` and its `TeamRow` both say: the label, the club's crest, the rosa. One
 *  place, so `/teams` and the card of a person cannot say the same squad differently. The crest is
 *  decorative: every page that draws a squad names its club already. */
export function squadIdentity(
  team: TeamRead,
  club: SquadClub,
): Pick<TeamCardProps, 'title' | 'clubName' | 'crestUrl' | 'crestAlt' | 'players'> {
  return {
    title: squadLabel(team),
    clubName: club.name,
    crestUrl: club.crestUrl,
    crestAlt: '',
    players: squadPlayers(team),
  }
}

/** What the squad head's tile calls the same count: «Giocatori», «Giocatrici». */
export function playersTileLabel(team: Pick<TeamRead, 'gender'>): string {
  const plural = playersNoun(team, 2)
  return plural.charAt(0).toLocaleUpperCase() + plural.slice(1)
}

/** The coaches on a squad's card, withheld like the rosa's count: a null is not a zero. */
export function squadCoaches(team: TeamRead): TeamCardCount | undefined {
  if (team.coaches_count == null) return undefined
  const { one, many } = SQUAD_COACH_NOUN
  return { count: team.coaches_count, noun: team.coaches_count === 1 ? one : many }
}

/** «14 giocatori»: the rosa's count, in the word for the squad's gender and for that number. */
export function rosterCountLabel(team: Pick<TeamRead, 'gender'>, count: number): string {
  return interpolate(messages.team.rosterCount, { count, noun: playersNoun(team, count) })
}

/** «2° posto», or «2° posto · Girone B» when the girone has to be named. */
function placeLabel(position: number, girone: string | undefined): string {
  const ui = messages.official.dashboard
  if (girone === undefined) return interpolate(ui.squadPlace, { position })
  return interpolate(ui.squadPlaceIn, { position, code: girone })
}

/** One entry's chip, or none where the API gives no place — withdrawn, or nothing played. */
function placeOf(entry: TeamEnrollmentRead, named: boolean): readonly TeamCardPlace[] {
  if (entry.position == null) return []
  const girone = named ? entry.league_group.code : undefined
  return [{ label: placeLabel(entry.position, girone), first: entry.position === 1 }]
}

/** A squad's place in each girone it stands in, off the club's entries: the API ranks them,
 *  so the table stays in Campionati. The girone is named only for a squad in more than one,
 *  or two chips would not say which is which. */
export function squadPlaces(
  teamId: number,
  entries: readonly TeamEnrollmentRead[],
): readonly TeamCardPlace[] {
  const own = entriesOf(teamId, entries)
  return own.flatMap((entry) => placeOf(entry, own.length > 1))
}

/** A championship season spans two calendar years, and the label says both. */
export function seasonLabel(year: number): string {
  return `${String(year)}/${String((year + 1) % 100).padStart(2, '0')}`
}

/** «girone A · 2026/27» — the metadata line under a girone's name, wherever one is listed.
 *  Structural, so both `LeagueGroupRead` and a nested one fit. */
export function groupMetadata(group: { code: string; season_year: number }): string {
  const code = interpolate(messages.leagues.groupCode, { code: group.code })
  return `${code} · ${seasonLabel(group.season_year)}`
}

/** What a `GironeRow` says of a girone: its championship, and the metadata line under it with
 *  whose side it is where the row needs it. Spread into the row, as `faceOf` into a person's. */
export function gironeOf(
  group: { code: string; season_year: number; category: { name: string } },
  side?: string,
): { readonly name: string; readonly detail: string } {
  const metadata = groupMetadata(group)
  return {
    name: group.category.name,
    detail: side === undefined ? metadata : `${metadata} · ${side}`,
  }
}

/** The warning a squad's entry wears once it has withdrawn, and nothing while it plays on. */
export function withdrawnLabel(
  entry: Pick<TeamEnrollmentRead, 'has_withdrawn'>,
): string | undefined {
  return entry.has_withdrawn ? messages.leagues.withdrawn : undefined
}

/** From the label map's own keys rather than a second list: it is typed
 *  `Record<AppointmentRole, string>`, so a role added to the contract fails the catalogue. */
export function appointmentRoleOptions(): readonly { value: AppointmentRole; label: string }[] {
  return (Object.keys(APPOINTMENT_ROLES) as AppointmentRole[]).map((value) => ({
    value,
    label: APPOINTMENT_ROLES[value],
  }))
}

/** The role of an incarico as every row says it under a name — a squad's bench, a person's card, the
 *  staff list — so that a card and a row cannot say two words for the same role. */
export function appointmentRoleLabel(role: AppointmentRole): string {
  return translateDomainValue(APPOINTMENT_ROLES, role)
}

/** «Dirigente · Allenatore»: the roles a person holds in a società, each said once — a coach of two
 *  squads holds the one role twice —, in the order the incarico picker lists them. `undefined` for
 *  a person who holds none. */
export function heldRolesLine(
  held: readonly { readonly role: AppointmentRole }[],
): string | undefined {
  const roles = appointmentRoleOptions().filter((option) =>
    held.some((one) => one.role === option.value),
  )
  return detailLine(roles.map((option) => option.label))
}

/** The role and nothing else: a season shirt number does not exist in the model — it is a
 *  column of `call_up`, per match. */
export function playerSummary(player: ClubPlayerRead): string {
  if (player.position == null) return messages.roster.noPosition
  return positionLabel(player.position)
}

/** «POR»: the role as the filter chips and the line under a name write it, in capitals. */
export function positionCode(position: PlayerPosition): string {
  return positionShortLabel(position).toUpperCase()
}

/** «ALLIEVI U17» — the leva for the match header, from `age_group` and `under_age`. No
 *  birth year: the model has no such column, and inventing one would be a lie. */
export function sideCategoryLabel(side: MatchSide): string {
  const ageGroup = translateDomainValue(AGE_GROUPS, side.age_group)
  if (side.age_group === 'seniores') return ageGroup
  if (side.under_age === 0) return interpolate(messages.match.sideCategoryBothYears, { ageGroup })
  return interpolate(messages.match.sideCategory, { ageGroup, underAge: side.under_age })
}

/** What names a side: the club, and the letter where the club fields more than one squad in
 *  a category. Also fits a `StandingsRow`, which holds both those columns and nothing else of
 *  the squad. */
type NamedSide = Pick<MatchSide, 'club_short_name' | 'letter'>

/** A side named in a sentence — «vs Lavagnese · Squadra B». The two Lavagnese sides of Pulcini
 *  2° anno girone A (C.U. 44 di Chiavari) play each other, and the club's name alone writes
 *  that fixture as «Lavagnese – Lavagnese». */
export function sideName(side: NamedSide): string {
  return detailLine([side.club_short_name, squadLetter(side.letter)])
}

/** A side as a `MatchRow` renders it. Shared, because a list that composes the name by hand
 *  is the list whose crests silently go missing — and `squad` stays out of `name` because the
 *  shield reads the club's initials off it. */
export function crestedSide(side: MatchSide): { name: string; squad?: string; crestUrl?: string } {
  return {
    crestUrl: side.logo_display_url ?? undefined,
    name: side.club_short_name,
    squad: squadLetter(side.letter),
  }
}

/** A side as the match header renders it: club, leva, and a province the header omits
 *  entirely when it is absent — never «— · —». */
export function matchHeaderSide(side: MatchSide): MatchHeaderSide {
  return {
    name: side.club_short_name,
    squad: squadLetter(side.letter),
    crestUrl: side.logo_display_url ?? undefined,
    province: side.club_province ?? undefined,
  }
}

/** The girone a match is played in, as its category and code say it: «Terza Categoria Genova ·
 *  girone C». The code alone names nothing, every girone of a province being «A». */
export function competitionLabel(
  match: Pick<MatchRead, 'league_category_name' | 'league_group_code'>,
): string {
  return interpolate(messages.match.competition, {
    competition: match.league_category_name,
    group: match.league_group_code,
  })
}

/** How the match stands, for the pill under the score: «Finale» once played, «Rinviata» when put
 *  off, and nothing for a fixture still to come — it has no standing to state. */
export function matchStatusLabel(match: Pick<MatchRead, 'status'>): string | undefined {
  if (match.status === 'played') return messages.match.statusFinal
  if (match.status === 'postponed') return translateDomainValue(MATCH_STATUSES, match.status)
  return undefined
}

/** What a cell says of a fact the API has none of, where a blank would read as forgotten. */
export const NO_VALUE = '—'

/** An em dash and not `0 – 0` while the goals are unknown: a match not played has no score.
 *  Home first, or `from`'s goals first when one side is the reader's own. */
export function scoreOf(match: MatchRead, from: MatchSide = match.home): string {
  if (match.home_goals == null || match.away_goals == null) return NO_VALUE
  const [first, second] =
    from === match.away
      ? [match.away_goals, match.home_goals]
      : [match.home_goals, match.away_goals]
  return `${String(first)} – ${String(second)}`
}
